import { FunctionComponent } from "react";
import {
    Badge,
    Box,
    Card,
    CardBody,
    Divider,
    Grid,
    Heading,
    Spinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    VStack,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { formatTimestampStr } from "@am-tax/fe-core";
import { SetPageTitle } from "../components/SetPageTitle";
import { useClient } from "../api";
import { useUserContext } from "../context";

const SectionHeader: FunctionComponent<{ title: string; description?: string }> = ({ title, description }) => (
    <Box mb={6}>
        <Heading size="md" mb={1}>
            {title}
        </Heading>
        {description && (
            <Text fontSize="sm" color="gray.500">
                {description}
            </Text>
        )}
        <Divider mt={4} />
    </Box>
);

const FieldRow: FunctionComponent<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" color="gray.500" mb={1}>
            {label}
        </Text>
        {typeof value === "string" ? <Text fontSize="sm">{value || "—"}</Text> : value}
    </Box>
);

const CandidateFieldRow: FunctionComponent<{ fieldNumber: number; displayName?: string | null; shownInGrid?: boolean }> = ({
    fieldNumber,
    displayName,
    shownInGrid,
}) => (
    <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={4}>
        <FieldRow label={`Field ${fieldNumber} display name`} value={displayName ?? "—"} />
        <FieldRow label="Shown in grid" value={shownInGrid ? "Yes" : "No"} />
    </Grid>
);

export const ClientInfoPage: FunctionComponent = () => {
    const { cId: clientId } = useParams();
    const { dateFormat } = useUserContext();
    const clientQuery = useClient(clientId, {
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        staleTime: 0,
    });
    const client = clientQuery.data;

    if (clientQuery.isLoading) {
        return <Spinner />;
    }

    if (!client) {
        return <Text color="gray.500">Client not found.</Text>;
    }

    const candidateFieldsConfiguration = client.candidateFieldsConfiguration;

    return (
        <Box>
            <SetPageTitle title={client.name ? `${client.name} — Client Info` : "Client Info"} />
            <Heading size="lg" mb={2}>
                Client Info
            </Heading>
            <Text color="gray.500" mb={6}>
                Read-only client configuration and system metadata.
            </Text>

            <Card variant="withShadow">
                <CardBody>
                    <Tabs colorScheme="blue" variant="enclosed">
                        <TabList>
                            <Tab>Settings</Tab>
                            <Tab>Custom Fields</Tab>
                            <Tab>System</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel px={0} pt={6}>
                                <SectionHeader title="Settings" description="Basic client information and availability." />
                                <VStack spacing={5} align="stretch">
                                    <FieldRow
                                        label="Status"
                                        value={
                                            <Badge colorScheme={client.isEnabled ? "green" : "gray"} fontSize="sm" px={2} py={1} borderRadius="md">
                                                {client.isEnabled ? "Enabled" : "Disabled"}
                                            </Badge>
                                        }
                                    />
                                    <FieldRow label="Name" value={client.name} />
                                    <FieldRow label="Description" value={client.description ?? "—"} />
                                    <FieldRow label="External ID" value={client.externalId ?? "—"} />
                                </VStack>
                            </TabPanel>
                            <TabPanel px={0} pt={6}>
                                <SectionHeader
                                    title="Integration Custom Fields"
                                    description="Arbitrary data fields for integration or organizational purposes."
                                />
                                <VStack spacing={5} align="stretch" mb={8}>
                                    <FieldRow label="Custom field 1" value={client.customField01 ?? "—"} />
                                    <FieldRow label="Custom field 2" value={client.customField02 ?? "—"} />
                                    <FieldRow label="Custom field 3" value={client.customField03 ?? "—"} />
                                    <FieldRow label="Custom field 4" value={client.customField04 ?? "—"} />
                                    <FieldRow label="Custom field 5" value={client.customField05 ?? "—"} />
                                </VStack>

                                <SectionHeader
                                    title="Candidate Custom Fields"
                                    description="Display names and grid visibility for candidate custom fields."
                                />
                                <VStack spacing={5} align="stretch">
                                    <CandidateFieldRow
                                        fieldNumber={1}
                                        displayName={candidateFieldsConfiguration?.customField01DisplayName}
                                        shownInGrid={candidateFieldsConfiguration?.isCustomField01ShownInGrid}
                                    />
                                    <CandidateFieldRow
                                        fieldNumber={2}
                                        displayName={candidateFieldsConfiguration?.customField02DisplayName}
                                        shownInGrid={candidateFieldsConfiguration?.isCustomField02ShownInGrid}
                                    />
                                    <CandidateFieldRow
                                        fieldNumber={3}
                                        displayName={candidateFieldsConfiguration?.customField03DisplayName}
                                        shownInGrid={candidateFieldsConfiguration?.isCustomField03ShownInGrid}
                                    />
                                    <CandidateFieldRow
                                        fieldNumber={4}
                                        displayName={candidateFieldsConfiguration?.customField04DisplayName}
                                        shownInGrid={candidateFieldsConfiguration?.isCustomField04ShownInGrid}
                                    />
                                    <CandidateFieldRow
                                        fieldNumber={5}
                                        displayName={candidateFieldsConfiguration?.customField05DisplayName}
                                        shownInGrid={candidateFieldsConfiguration?.isCustomField05ShownInGrid}
                                    />
                                </VStack>
                            </TabPanel>
                            <TabPanel px={0} pt={6}>
                                <SectionHeader title="System" description="Read-only system metadata." />
                                <VStack spacing={5} align="stretch">
                                    <FieldRow
                                        label="Client ID"
                                        value={
                                            <Text fontSize="sm" fontFamily="mono">
                                                {client.id}
                                            </Text>
                                        }
                                    />
                                    <FieldRow label="Created by" value={client.createdByDisplayName ?? "—"} />
                                    <FieldRow
                                        label="Created on"
                                        value={client.createdOn ? formatTimestampStr(client.createdOn, dateFormat) : "—"}
                                    />
                                    <FieldRow label="Updated by" value={client.updatedByDisplayName ?? "—"} />
                                    <FieldRow
                                        label="Updated on"
                                        value={client.updatedOn ? formatTimestampStr(client.updatedOn, dateFormat) : "—"}
                                    />
                                </VStack>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </CardBody>
            </Card>
        </Box>
    );
};
